import { useSearchParams } from "react-router-dom";
import Tabs, { SingleTab } from "../../../common/components/Tabs";
import { DefaultTabContent } from "../../../common/components/DefaultTabContent";
import GeneralTab from "./GeneralTab";
import {
	ContractDetailType,
	ContractPeriodType,
} from "../../../requests_cm/gecoContractsService/types";
import { PriceIndex } from "../../../requests_cm/gecoReferentialService/types";
import { SettlementTab } from "./SettlementTab";
import ClickabilityTab from "./ClickabilityTab";
import ServicePointsTab from "./ServicePointsTab";
import DealsTab from "./DealsTab";
import CurtailmentObjectTab from "./CurtailmentObjectTab";
import { isFalsy } from "../../../common/utils/isFalsy";
import { ClickModalProvider } from "./ClickabilityTab/ClickModalContext";

export enum TabID {
	GENERAL = "general",
	CLICKABILITY = "clickability",
	SETTLEMENT_OBJECT = "settlement_object",
	SERVICE_POINT = "service_point",
	CURTAILMENT_OBJECT = "curtailment_object",
	DEAL = "deal",
	FIELD_DATA = "field_data",
}

export const resolvePreloadTab = (tab: string | null): TabID =>
	tab && Object.values(TabID).includes(tab as TabID)
		? (tab as TabID)
		: TabID.GENERAL;

export const withTabParam = (
	searchParams: URLSearchParams,
	tab: TabID
): URLSearchParams => {
	const next = new URLSearchParams(searchParams);
	next.set("tab", tab);
	return next;
};

export interface ContractPeriodTabsProps {
	contract: ContractDetailType;
	contractPeriod: ContractPeriodType;
	onSaveDraftContract: (data: ContractDetailType) => void;
	negativePriceIndexes: PriceIndex[] | undefined;
	setHorizonChangeOpen: () => void;
}

const ContractPeriodTabs = ({
	contractPeriod,
	onSaveDraftContract,
	contract,
	negativePriceIndexes,
	setHorizonChangeOpen,
}: ContractPeriodTabsProps) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTab = resolvePreloadTab(searchParams.get("tab"));

	return (
		<Tabs
			activeTab={activeTab}
			label={""}
			onClickTabItem={(tab: TabID) =>
				setSearchParams(withTabParam(searchParams, tab), {
					replace: true,
				})
			}
		>
			<SingleTab label="General" id={TabID.GENERAL}>
				<DefaultTabContent>
					<GeneralTab
						onSaveDraftContract={onSaveDraftContract}
						contract={contract}
						contractPeriod={contractPeriod}
						setHorizonChangeOpen={setHorizonChangeOpen}
					/>
				</DefaultTabContent>
			</SingleTab>
			<SingleTab
				label="Clickability"
				id={TabID.CLICKABILITY}
				isDisabled={isFalsy(contractPeriod?.clickability)}
			>
				<ClickModalProvider>
					<ClickabilityTab
						contractPeriodId={contractPeriod?.id}
						onSaveDraftContract={onSaveDraftContract}
						contract={contract}
					/>
				</ClickModalProvider>
			</SingleTab>
			<SingleTab label="Settlement Object" id={TabID.SETTLEMENT_OBJECT}>
				<SettlementTab
					contract={contract}
					onSaveDraftContract={onSaveDraftContract}
					contractPeriodId={contractPeriod?.id}
					negativePriceIndexes={negativePriceIndexes}
				/>
			</SingleTab>
			<SingleTab label="Service Point" id={TabID.SERVICE_POINT}>
				<ServicePointsTab contractPeriod={contractPeriod} />
			</SingleTab>
			<SingleTab label="Curtailment Object" id={TabID.CURTAILMENT_OBJECT}>
				<CurtailmentObjectTab
					contractPeriodId={contractPeriod.id}
					country={contract.country}
				/>
			</SingleTab>
			<SingleTab label="Deals" id={TabID.DEAL}>
				<DealsTab
					contractPeriodId={contractPeriod.id}
					contractPeriodStartDate={contractPeriod.start_date}
					contractPeriodEndDate={contractPeriod.end_date}
				/>
			</SingleTab>
		</Tabs>
	);
};

export default ContractPeriodTabs;
